'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { resolveAppliedTheme, THEME_COOKIE, type ThemePreference } from '@/utils/theme';

/**
 * The theme context surface: the current {@link ThemePreference} and a setter that applies it,
 * mirrors it to the cookie, and (for `'system'`) keeps tracking the OS signal.
 */
export interface ThemeContextValue {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}
const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Applies a preference to `<html data-theme>`, resolving `'system'` against the live OS signal.
 *
 * @param preference - The preference to apply.
 */
function applyPreference(preference: ThemePreference): void {
  const prefersLight = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches;
  document.documentElement.dataset.theme = resolveAppliedTheme(preference, prefersLight);
}

/**
 * Shell-level theme controller. Holds the operator's {@link ThemePreference}, applies it to
 * `<html data-theme>`, mirrors changes to the `admin-theme` cookie (for no-flash SSR), and — while on
 * `'system'` — re-applies when the OS scheme flips. Persistence to the user record is the caller's job
 * (the account toggle calls the server action), keeping this provider serializable from the server
 * layout and decoupled from any one feature.
 *
 * @param props.initialPreference - The server-resolved preference (from the cookie).
 * @param props.children - The app tree.
 */
export function ThemeProvider(t0) {
  const $ = _c(9);
  const {
    initialPreference,
    children
  } = t0;
  const [preference, setPreferenceState] = useState(initialPreference);
  let t1;
  let t2;
  if ($[0] !== preference) {
    t1 = () => {
      applyPreference(preference);
      if (preference !== "system") {
        return;
      }
      const media = window.matchMedia("(prefers-color-scheme: light)");
      const handler = _temp;
      media.addEventListener("change", handler);
      return () => media.removeEventListener("change", handler);
    };
    t2 = [preference];
    $[0] = preference;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = next => {
      setPreferenceState(next);
      applyPreference(next);
      document.cookie = `${THEME_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setPreference = t3;
  let t4;
  if ($[4] !== preference) {
    t4 = {
      preference,
      setPreference
    };
    $[4] = preference;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const value = t4;
  let t5;
  if ($[6] !== children || $[7] !== value) {
    t5 = <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
    $[6] = children;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

/**
 * Reads the theme context.
 *
 * @returns The current preference and its setter.
 * @throws {Error} When called outside a {@link ThemeProvider}.
 */
function _temp() {
  return applyPreference("system");
}
export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme must be used within a ThemeProvider.");
  }
  return value;
}